import { useSelector } from 'react-redux';

import { CoreSelectProps, GroupProps, SelectNode } from '../types/props';

import { defaultOrder } from './default-order';
import { getName } from './get-name';

import { SelectList, GeneralKey, getSelectItemState } from '@app/store/SelectList/SelectList';
import { SelectRequestParams } from '@app/store/SelectList/ListActions';
import { SelectRequestProps } from '@app/store/SelectList/SelectsInterfaces';
import { selectListRequest } from '@app/store/SelectList/SelectListRequests';
import { StoreType, EntityType } from '@app/store/SelectList/UtilityTypes';
import { ApplicationState } from '@app/store';

export function useOptions<
	TStore extends keyof SelectList,
	TParams extends SelectRequestParams<number>
>(props: CoreSelectProps<number, TStore, TParams>): Array<SelectNode> {
	const { order = false } = props;
	const reqProps: SelectRequestProps<TStore> = selectListRequest[props.store];

	const key = props.storeKey ?? GeneralKey;
	const root: StoreType<TStore> = useSelector<ApplicationState, StoreType<TStore>>((state: ApplicationState) =>
		state.selects[props.store]);
	const store = getSelectItemState(root, key);

	let newOrder = order ? reqProps.order : defaultOrder;
	if (typeof order === 'function') newOrder = order;
	if (typeof newOrder === 'undefined') newOrder = defaultOrder;

	const localOptions = props.localOptions ?? [];
	const options: Array<EntityType<TStore>> = [...store.items, ...localOptions] as Array<EntityType<TStore>>;
	let nodes: Array<SelectNode> = [];

	if (props.group) {
		nodes = props.group(options).map((item: GroupProps<EntityType<TStore>>) => ({
			label: item.title,
			options: newOrder(item.options)
				.map((item) => ({
					label: getName(item, props.getName),
					value: item.id,
				})),
		}));
	} else {
		nodes = newOrder(options)
			.map((item: EntityType<TStore>) => ({
				label: getName(item, props.getName),
				value: item.id,
			}));
	}

	return nodes;
}
